import { useCallback, useEffect, useRef, useState } from "react";
import { fetchVisitors, fetchStats, WS_URL } from "@/lib/api";

export function useRegistre(filter) {
  const [visitors, setVisitors] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const filterRef = useRef(filter);
  filterRef.current = filter;

  const buildParams = useCallback((f) => {
    const p = { period: f.period };
    if (f.period === "custom") {
      if (f.from) p.frm = f.from;
      if (f.to) p.to = f.to;
    }
    if (f.site_id) p.site_id = f.site_id;
    return p;
  }, []);

  const refresh = useCallback(async () => {
    try {
      const params = buildParams(filterRef.current);
      const statParams = filterRef.current.site_id ? { site_id: filterRef.current.site_id } : {};
      const [v, s] = await Promise.all([fetchVisitors(params), fetchStats(statParams)]);
      setVisitors(v);
      setStats(s);
    } catch (e) {
      // silent, keep last data
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    setLoading(true);
    refresh();
  }, [filter.period, filter.from, filter.to, filter.site_id, refresh]);

  useEffect(() => {
    let alive = true;
    let reconnectTimer = null;

    const connect = () => {
      try {
        const ws = new WebSocket(WS_URL);
        wsRef.current = ws;
        ws.onopen = () => alive && setConnected(true);
        ws.onmessage = () => refresh();
        ws.onclose = () => {
          if (!alive) return;
          setConnected(false);
          reconnectTimer = setTimeout(connect, 3000);
        };
        ws.onerror = () => ws.close();
      } catch (e) {
        reconnectTimer = setTimeout(connect, 3000);
      }
    };
    connect();

    // Fallback polling every 15s in case websocket is unavailable
    const poll = setInterval(refresh, 15000);

    return () => {
      alive = false;
      clearInterval(poll);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (wsRef.current) wsRef.current.close();
    };
  }, [refresh]);

  return { visitors, stats, loading, connected, refresh, setVisitors };
}
